import { Box, Button, HStack, Spinner, Text, VStack } from "@chakra-ui/react"
import type React from "react"
import { Skeleton } from "../Skeleton"

// =============================================================================
// TYPES & INTERFACES
// =============================================================================

export type LoadingSize = "xs" | "sm" | "md" | "lg" | "xl"
export type LoadingLevel = "page" | "feature" | "component" | "inline"
export type SkeletonVariant =
  | "text"
  | "card"
  | "table"
  | "circle"
  | "rectangle"
  | "custom"

interface BaseLoadingProps {
  size?: LoadingSize
  message?: string
  testId?: string
  className?: string
}

interface LoadingSpinnerProps extends BaseLoadingProps {
  color?: string
  thickness?: string
}

interface LoadingSkeletonProps extends BaseLoadingProps {
  variant?: SkeletonVariant
  lines?: number
  width?: string | number
  height?: string | number
  isLoaded?: boolean
  children?: React.ReactNode
}

interface LoadingDotsProps extends BaseLoadingProps {
  color?: string
  count?: number
}

interface ButtonLoadingProps {
  isLoading: boolean
  loadingText?: string
  children: React.ReactNode
  size?: LoadingSize
  [key: string]: any
}

interface PageLoadingProps {
  title?: string
  message?: string
  showLogo?: boolean
}

interface FeatureLoadingProps {
  message?: string
  variant?: "spinner" | "skeleton" | "dots"
  skeletonVariant?: SkeletonVariant
}

interface ComponentLoadingProps {
  variant?: "spinner" | "skeleton" | "dots"
  size?: LoadingSize
  inline?: boolean
}

// =============================================================================
// ANIMATIONS - Inline keyframe definitions for Chakra UI v3 compatibility
// =============================================================================

// =============================================================================
// UTILITY FUNCTIONS
// =============================================================================

const getSizeProps = (size: LoadingSize = "md") => {
  const sizeMap = {
    xs: { spinner: "12px", text: "xs", spacing: 1, dots: "4px" },
    sm: { spinner: "16px", text: "sm", spacing: 2, dots: "6px" },
    md: { spinner: "24px", text: "md", spacing: 3, dots: "8px" },
    lg: { spinner: "32px", text: "lg", spacing: 4, dots: "10px" },
    xl: { spinner: "48px", text: "xl", spacing: 6, dots: "12px" },
  }
  return sizeMap[size]
}

// =============================================================================
// BASIC LOADING COMPONENTS
// =============================================================================

/**
 * Standard loading spinner with customizable appearance
 */
export const LoadingSpinner: React.FC<LoadingSpinnerProps> = ({
  size = "md",
  message,
  color = "blue.500",
  thickness = "3px",
  testId = "loading-spinner",
  className,
}) => {
  const { spinner, text, spacing } = getSizeProps(size)

  return (
    <VStack gap={spacing} className={className} data-testid={testId}>
      <Spinner
        boxSize={spinner}
        color={color}
        borderWidth={thickness}
        role="status"
        aria-label={message || "Loading content"}
      />
      {message && (
        <Text
          fontSize={text}
          color="gray.600"
          textAlign="center"
          aria-live="polite"
        >
          {message}
        </Text>
      )}
    </VStack>
  )
}

/**
 * Flexible skeleton loading component with multiple variants
 */
export const LoadingSkeleton: React.FC<LoadingSkeletonProps> = ({
  variant = "text",
  lines = 3,
  width = "100%",
  height,
  size = "md",
  message,
  isLoaded = false,
  children,
  testId = "loading-skeleton",
  className,
}) => {
  const { text, spacing } = getSizeProps(size)

  // If content is loaded, show children
  if (isLoaded && children) {
    return <>{children}</>
  }

  const renderSkeletonVariant = () => {
    switch (variant) {
      case "text":
        return (
          <VStack gap={2} align="stretch" width={width}>
            {Array.from({ length: lines }, (_, i) => (
              <Skeleton
                key={i}
                height="1em"
                width={i === lines - 1 ? "60%" : "100%"}
                borderRadius="md"
              />
            ))}
          </VStack>
        )

      case "card":
        return (
          <Box width={width} p={4} borderWidth="1px" borderRadius="lg">
            <VStack gap={3} align="stretch">
              <HStack gap={3}>
                <Skeleton height="40px" width="40px" borderRadius="full" />
                <VStack gap={1} align="stretch" flex="1">
                  <Skeleton height="1em" width="60%" />
                  <Skeleton height="0.8em" width="40%" />
                </VStack>
              </HStack>
              <VStack gap={2} align="stretch">
                {Array.from({ length: 2 }, (_, i) => (
                  <Skeleton key={i} height="0.9em" width="100%" />
                ))}
                <Skeleton height="0.9em" width="70%" />
              </VStack>
            </VStack>
          </Box>
        )

      case "table":
        return (
          <VStack gap={2} align="stretch" width={width}>
            {Array.from({ length: lines }, (_, i) => (
              <HStack key={i} gap={4}>
                <Skeleton height="2em" width="20%" />
                <Skeleton height="2em" width="30%" />
                <Skeleton height="2em" width="25%" />
                <Skeleton height="2em" width="25%" />
              </HStack>
            ))}
          </VStack>
        )

      case "circle":
        return (
          <Skeleton
            height={height || "80px"}
            width={height || "80px"}
            borderRadius="full"
          />
        )

      case "rectangle":
        return (
          <Skeleton
            height={height || "200px"}
            width={width}
            borderRadius="md"
          />
        )

      default:
        return (
          <Skeleton height={height || "1em"} width={width} borderRadius="md" />
        )
    }
  }

  return (
    <Box className={className} data-testid={testId}>
      {renderSkeletonVariant()}
      {message && (
        <Text
          fontSize={text}
          color="gray.500"
          textAlign="center"
          mt={spacing}
          aria-live="polite"
        >
          {message}
        </Text>
      )}
    </Box>
  )
}

/**
 * Animated loading dots component
 */
export const LoadingDots: React.FC<LoadingDotsProps> = ({
  size = "md",
  count = 3,
  color = "blue.500",
  message,
  testId = "loading-dots",
  className,
}) => {
  const { dots, text, spacing } = getSizeProps(size)

  const dotStyle: React.CSSProperties = {
    animation: "dotPulse 1.4s infinite ease-in-out both",
  }

  return (
    <>
      <style>
        {`
          @keyframes dotPulse {
            0%, 80%, 100% { 
              transform: scale(0.8);
              opacity: 0.5;
            }
            40% { 
              transform: scale(1);
              opacity: 1;
            }
          }
        `}
      </style>
      <VStack gap={spacing} className={className} data-testid={testId}>
        <HStack gap={1} role="status" aria-label={message || "Loading"}>
          {Array.from({ length: count }, (_, i) => (
            <Box
              key={i}
              width={dots}
              height={dots}
              borderRadius="full"
              bg={color}
              style={{
                ...dotStyle,
                animationDelay: `${i * 0.16}s`,
              }}
              aria-hidden="true"
            />
          ))}
        </HStack>
        {message && (
          <Text
            fontSize={text}
            color="gray.600"
            textAlign="center"
            aria-live="polite"
          >
            {message}
          </Text>
        )}
      </VStack>
    </>
  )
}

/**
 * Button with integrated loading state
 */
export const ButtonLoading: React.FC<ButtonLoadingProps> = ({
  isLoading,
  loadingText = "Loading...",
  children,
  size = "md",
  disabled,
  ...buttonProps
}) => {
  const { spinner } = getSizeProps(size)

  return (
    <Button
      {...buttonProps}
      disabled={disabled || isLoading}
      aria-busy={isLoading}
      data-testid="button-loading"
    >
      {isLoading ? (
        <HStack gap={2}>
          <Spinner boxSize={spinner} />
          <Text>{loadingText}</Text>
        </HStack>
      ) : (
        children
      )}
    </Button>
  )
}

// =============================================================================
// LEVEL-SPECIFIC LOADING COMPONENTS
// =============================================================================

/**
 * Full-page loading component with branding
 */
export const PageLoading: React.FC<PageLoadingProps> = ({
  title = "Loading",
  message = "Please wait while we load your content...",
  showLogo = true,
  ...props
}) => (
  <>
    <style>
      {`
        @keyframes slideIn {
          from {
            opacity: 0;
            transform: translateY(10px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
      `}
    </style>
    <Box
      display="flex"
      flexDirection="column"
      alignItems="center"
      justifyContent="center"
      minHeight="100vh"
      p={8}
      bg="gray.50"
      role="status"
      aria-label="Page loading"
      data-testid="page-loading"
      style={{
        animation: "slideIn 0.3s ease-out",
      }}
      {...props}
    >
      <VStack gap={6} textAlign="center" maxW="md">
        {showLogo && (
          <Box
            w="64px"
            h="64px"
            bg="blue.500"
            borderRadius="xl"
            display="flex"
            alignItems="center"
            justifyContent="center"
            color="white"
            fontSize="2xl"
            fontWeight="bold"
            aria-hidden="true"
          >
            ⚡
          </Box>
        )}

        <VStack gap={3}>
          <Text fontSize="xl" fontWeight="semibold" color="gray.800">
            {title}
          </Text>
          <Text color="gray.600" lineHeight="tall">
            {message}
          </Text>
        </VStack>

        <LoadingSpinner size="lg" />
      </VStack>
    </Box>
  </>
)

/**
 * Feature-level loading component
 */
export const FeatureLoading: React.FC<FeatureLoadingProps> = ({
  message = "Loading feature...",
  variant = "spinner",
  skeletonVariant = "card",
  ...props
}) => (
  <Box
    p={6}
    borderWidth="1px"
    borderColor="gray.200"
    borderRadius="lg"
    bg="gray.50"
    role="status"
    aria-label="Feature loading"
    data-testid="feature-loading"
    {...props}
  >
    {variant === "spinner" && <LoadingSpinner size="md" message={message} />}
    {variant === "skeleton" && (
      <LoadingSkeleton variant={skeletonVariant} message={message} />
    )}
    {variant === "dots" && <LoadingDots size="md" message={message} />}
  </Box>
)

/**
 * Component-level loading
 */
export const ComponentLoading: React.FC<ComponentLoadingProps> = ({
  variant = "spinner",
  size = "sm",
  inline = false,
  ...props
}) => {
  const Component = inline ? HStack : VStack

  return (
    <Component
      gap={2}
      justify="center"
      align="center"
      p={inline ? 2 : 4}
      role="status"
      aria-label="Component loading"
      data-testid="component-loading"
      {...props}
    >
      {variant === "spinner" && <LoadingSpinner size={size} />}
      {variant === "skeleton" && <LoadingSkeleton variant="text" lines={1} />}
      {variant === "dots" && <LoadingDots size={size} />}
    </Component>
  )
}

/**
 * Inline loading for small spaces
 */
export const InlineLoading: React.FC<
  BaseLoadingProps & {
    variant?: "spinner" | "dots"
  }
> = ({ variant = "spinner", size = "xs", message, ...props }) => (
  <HStack
    gap={2}
    display="inline-flex"
    role="status"
    aria-label={message || "Loading"}
    data-testid="inline-loading"
    {...props}
  >
    {variant === "spinner" && <LoadingSpinner size={size} />}
    {variant === "dots" && <LoadingDots size={size} count={3} />}
    {message && (
      <Text fontSize="sm" color="gray.600">
        {message}
      </Text>
    )}
  </HStack>
)

// =============================================================================
// EXPORTS
// =============================================================================

export default LoadingSpinner

// Grouped exports for easier importing
export const Loading = {
  Spinner: LoadingSpinner,
  Skeleton: LoadingSkeleton,
  Dots: LoadingDots,
  Button: ButtonLoading,
  Page: PageLoading,
  Feature: FeatureLoading,
  Component: ComponentLoading,
  Inline: InlineLoading,
}
